import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import Select from "react-select"
import { MIN_ASSISTANT_INSTRUCTIONS_LENGTH, MAX_ASSISTANT_INSTRUCTIONS_LENGTH, shareAccessLevels, singleSelectStyles, userRoles } from "../../helpers/constant";
import { ModelPicker } from "../../helpers/ModelPicker";
import { useDashboardUI } from "../../context/GlobalContext";
import promptService from "../../services/promptService";
import { extractUserFromToken } from "../../helpers/auth";
import { validateUserRole } from "../../helpers/helpers";
import assistantService from "../../services/assistantService";
import chatCategoryService from "../../services/chatCategoryService";
import { useNavigate, useParams } from "react-router-dom";
import SchemaBuilder from "../../components/schema-builder/SchemaBuilder";
import UserAccessPicker from "../../components/assistants/UserAccessPicker";
import { Button } from "../../components/ui/button";
import {
    validateSchema,
    hydrateSchema,
    canonicalSchema,
    deepEqualUnordered,
} from "../../components/schema-builder/schemaConstants";


export const ResponseFormatEnum = {
    default: "default",
    text: "text",
    json_object: "json_object",
    code_block: "code_block"
}

const NAME_MIN_LENGTH = 10
const NAME_MAX_LENGTH = 50
const DESCRIPTION_MIN_LENGTH = 20
const DESCRIPTION_MAX_LENGTH = 150

export default function CreateAssistant() {

    const navigate = useNavigate();
    const { assistantId } = useParams();
    const isEditMode = Boolean(assistantId)

    const currentUser = extractUserFromToken();
    const userFacilityId = parseInt(currentUser?.facility_id);

    const { selectedFacilityFilter, setPageLoader, facilityList, llmModels, facilityUserList } = useDashboardUI();

    const [selectedModel, setSelectedModel] = useState(null)
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [promptList, setPromptList] = useState([])
    const [errors, setErrors] = useState({})
    const [schemaErrors, setSchemaErrors] = useState({})
    const [isPublished, setIsPublished] = useState(false)

    const [shareAccess, setShareAccess] = useState(shareAccessLevels.FACILITY);
    const [selectedFacilities, setSelectedFacilities] = useState([userFacilityId]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [initialSelectedUsers, setInitialSelectedUsers] = useState([]);
    const [pendingShareAccessChange, setPendingShareAccessChange] = useState(null);
    const maxContextLength = selectedModel?.global_max_tokens ?? 32768
    const [submitted, setSubmitted] = useState(false)

    //Update Assistant
    const [assistantDetails, setAssistantDetails] = useState(null)
    const [initialSelectedFacilities, setInitialSelectedFacilities] = useState([])

    const facilityOptions = facilityList.map((f) => ({
        value: f.facility_number,
        label: f.name,
    }));

    let mode = isEditMode ? "Update" : "Create"

    const [form, setForm] = useState({
        name: "",
        description: "",
        system_instructions: "",
        template: "",
        projects: [],
        collection: "",
        file_ids: [],
        response_format: "default",
        output_schema: { schema_name: "", fields: [] },
        temperature: 0.8,
        top_k: 40,
        context_length: 8192,
        tools: [],
        is_image_mode: false,
        category_ids: [],
    });

    const [categoryOptions, setCategoryOptions] = useState([]);

    useEffect(() => {
        chatCategoryService
            .getAllCategories()
            .then((data) => setCategoryOptions(data?.categories || []))
            .catch(() => setCategoryOptions([]));
    }, []);

    // Track previous response_format to detect switching away from json_object
    const prevResponseFormat = useRef(form.response_format);

    const maxFiles = 10;

    const update = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleFileUpload = (e) => {
        const selected = Array.from(e.target.files);
        if (form.file_ids?.length + selected?.length > maxFiles) {
            toast.error("Maximum 10 files allowed");
            return;
        }
        update("file_ids", [...form.file_ids, ...selected.map(f => f.id)]);
    };

    const validate = () => {
        if (!form.name.trim()) {
            toast.error("Name is required");
            return false;
        }
        if (form.description.length < 20 || form.description.length > 150) {
            toast.error("Description must be between 20 and 150 characters");
            return false;
        }
        if (form.system_instructions.length < MIN_ASSISTANT_INSTRUCTIONS_LENGTH) {
            toast.error(`Instructions must be minimum ${MIN_ASSISTANT_INSTRUCTIONS_LENGTH} characters`);
            return false;
        }
        if (!selectedModel) {
            toast.error("Please select a model");
            return false;
        }
        // Validate structured output schema if json_object is selected
        if (form.response_format === ResponseFormatEnum.json_object && form.output_schema) {
            if (form.output_schema.fields.length === 0) {
                setSchemaErrors({});
                toast.error("Please add at least 1 field to the Structured output schema");
                return false;
            }
            const nextSchemaErrors = validateSchema(form.output_schema);
            setSchemaErrors(nextSchemaErrors);
            if (Object.keys(nextSchemaErrors).length > 0) {
                toast.error("Please fix the errors in the Structured output schema fields");
                return false;
            }
        } else {
            setSchemaErrors({});
        }
        return true;
    };

    const handleSubmit = async () => {

        setSubmitted(true);

        // Stop if form validation fails
        if (!validate()) return;

        try {
            setPageLoader(true);

            // Canonicalize the current schema and compare against what was loaded so
            // `output_schema` is only included in the payload when it actually differs.
            const outputSchemaPayload =
                form.response_format === "json_object"
                    ? canonicalSchema(form.output_schema)
                    : null;

            const initialSchemaCanonical = canonicalSchema(assistantDetails?.output_schema);
            const schemaChanged = !deepEqualUnordered(
                outputSchemaPayload,
                initialSchemaCanonical
            );

            // Detect if user switched away from json_object (need to tell backend to clear)
            const shouldClearSchema =
                prevResponseFormat.current === "json_object" &&
                form.response_format !== "json_object";

            const createAssistantPayload = {
                name: form.name,
                description: form.description,
                system_instructions: form.system_instructions,
                model_id: selectedModel?.global_model_id,
                model_configuration_code: selectedModel?.model_configuration_code,
                temperature: form.temperature,
                context_length: form.context_length,
                top_k: form.top_k,
                response_format: form.response_format,
                is_phi: false,
                is_published: isPublished,
                is_image_mode: form.is_image_mode,
                tools: form.tools,
                category_ids: form.category_ids,
                ...(outputSchemaPayload && schemaChanged && { output_schema: outputSchemaPayload }),
                ...(shouldClearSchema && { clear_output_schema: true }),
            };

            // Handle sharing logic on CREATE
            if (!isEditMode && canManageShareAccess) {
                createAssistantPayload.share_access = shareAccess;

                if (shareAccess === shareAccessLevels.FACILITY) {
                    if (selectedFacilities?.length === 0) {
                        toast.error("Please select at least one facility");
                        return;
                    }
                    createAssistantPayload.facility_numbers = selectedFacilities;
                } else if (shareAccess === shareAccessLevels.USERS) {
                    if (selectedUsers?.length === 0) {
                        toast.error("Please select at least one user");
                        return;
                    }
                    createAssistantPayload.user_ids = selectedUsers;
                }
            }

            if (isEditMode) {
                const updatedPayload = getUpdatedFields();
                const originalShareAccess = assistantDetails?.share_access;
                const shareAccessChanged = shareAccess !== originalShareAccess;

                // Backend reconciler fires when USERS is on either side of the
                // transition; in that case all membership must travel in the
                // same PATCH and no separate facility/user_access call is made.
                const isUsersTransition =
                    shareAccessChanged &&
                    (originalShareAccess === shareAccessLevels.USERS ||
                        shareAccess === shareAccessLevels.USERS);

                if (isUsersTransition) {
                    if (shareAccess === shareAccessLevels.USERS) {
                        if (selectedUsers.length === 0) {
                            toast.error("Please select at least one user");
                            return;
                        }
                        updatedPayload.share_access = shareAccessLevels.USERS;
                        updatedPayload.user_ids = selectedUsers;
                    } else if (shareAccess === shareAccessLevels.FACILITY) {
                        if (selectedFacilities.length === 0) {
                            toast.error("Please select at least one facility");
                            return;
                        }
                        updatedPayload.share_access = shareAccessLevels.FACILITY;
                        updatedPayload.facility_numbers = selectedFacilities;
                    } else {
                        updatedPayload.share_access = shareAccess;
                    }

                    await assistantService.updateAssistant(
                        assistantDetails.id,
                        updatedPayload,
                    );

                    if (shareAccess === shareAccessLevels.USERS) {
                        setInitialSelectedUsers(selectedUsers);
                    }
                    if (shareAccess === shareAccessLevels.FACILITY) {
                        setInitialSelectedFacilities(selectedFacilities);
                    }
                } else {
                    const facilityDelta =
                        shareAccess === shareAccessLevels.FACILITY
                            ? getFacilityListDelta()
                            : null;
                    const userDelta =
                        shareAccess === shareAccessLevels.USERS
                            ? getUserListDelta()
                            : null;

                    const hasAssistantChanges =
                        Object.keys(updatedPayload).length > 0;
                    const hasFacilityChanges = !!facilityDelta;
                    const hasUserChanges = !!userDelta;

                    if (
                        !hasAssistantChanges &&
                        !hasFacilityChanges &&
                        !hasUserChanges
                    ) {
                        toast.info("No changes to save");
                        return;
                    }

                    if (hasAssistantChanges) {
                        await assistantService.updateAssistant(
                            assistantDetails.id,
                            updatedPayload,
                        );
                    }
                    if (hasFacilityChanges) {
                        await assistantService.updateAssistantFacilityAccessList(
                            assistantDetails.id,
                            facilityDelta,
                        );
                        setInitialSelectedFacilities(selectedFacilities);
                    }
                    if (hasUserChanges) {
                        await assistantService.updateAssistantUserAccessList(
                            assistantDetails.id,
                            userDelta,
                        );
                        setInitialSelectedUsers(selectedUsers);
                    }
                }
            } else {
                // API Call
                const res = await assistantService.createAssistant(createAssistantPayload);

                if (!res) {
                    throw new Error("Invalid server response");
                }

            }

            // Update list
            // setAssistantList((prev) => [res.data, ...prev]);

            toast.success("Assistant created successfully");

            setSubmitted(false);

            // Ensure loader stops before navigation
            setPageLoader(false);

            navigate("/assistants");
        } catch (error) {
            console.error("Error creating assistant:", error);

            toast.error(
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.message ||
                `Failed to ${mode.toLowerCase()} assistant`
            );
        } finally {
            // Safety fallback — ensures loader is always disabled
            setPageLoader(false);
        }
    };


    //Response Format option structure modified
    const responseFormatOptions = useMemo(
        () => {
            const supportsStructuredOutput = selectedModel?.supports_structured_output !== false;
            return Object.values(ResponseFormatEnum)
                .filter(val => {
                    // Hide json_object if model explicitly doesn't support it
                    if (val === "json_object" && !supportsStructuredOutput) return false;
                    return true;
                })
                .map(val => {
                    const label = val.replace(/_/g, " ")
                    return {
                        value: val,
                        label: label.charAt(0).toUpperCase() + label.slice(1)
                    }
                });
        },
        [selectedModel]
    )

    const handleRegenerateInstructions = async () => {
        try {
            const trimmedContent = form.system_instructions.trim()
            const contentLength = trimmedContent.length
            errors.system_instructions = ""

            if (!trimmedContent) {
                errors.system_instructions = `Please provide Instruction with a minimum length of ${MIN_ASSISTANT_INSTRUCTIONS_LENGTH} characters.`
            } else if (contentLength < MIN_ASSISTANT_INSTRUCTIONS_LENGTH) {
                errors.system_instructions = `Instruction must be at least ${MIN_ASSISTANT_INSTRUCTIONS_LENGTH} characters long.`
            } else if (contentLength > MAX_ASSISTANT_INSTRUCTIONS_LENGTH) {
                errors.system_instructions = `Instruction cannot exceed ${MAX_ASSISTANT_INSTRUCTIONS_LENGTH} characters.`
            }

            if (errors.system_instructions) {
                toast.error(errors.system_instructions)
                return
            }
            setIsRegenerating(true);

            const payload = { prompt: form.system_instructions };
            const res = await promptService.regeneratePrompt(payload);

            if (res && res.data && res.data.prompt) {
                let newPrompt = res.data.prompt.replace(/undefined$/i, "");

                // Start typing animation
                const typeOutText = (text, index = 0) => {
                    if (index > text.length) {
                        setIsRegenerating(false);
                        return;
                    }
                    update("system_instructions", text.slice(0, index));
                    setTimeout(() => typeOutText(text, index + 1), 20);
                };

                typeOutText(newPrompt);
            } else {
                setIsRegenerating(false);
            }
        } catch (err) {
            setIsRegenerating(false);
            console.error("Error handleRegenerateInstructions:", err);
        }
    };

    //Prompt Format option structure modified
    const promptOptions = useMemo(
        () =>
            (promptList || []).map((p) => ({
                value: p.id,
                label: p.name,
                content: p.content
            })),
        [promptList]
    )

    const fetchPromptlist = async () => {
        try {
            // setPageLoader(true);
            const facility_number = selectedFacilityFilter?.facility_number;

            let query = `&published=true`;

            if (facility_number) query += `&facility_number=${facility_number}`;

            const data = await promptService.getAllPrompts(query);

            setPromptList(data.results || []);

        } catch (error) {
            console.error("Error fetching prompt list:", error);
        }
    };

    const isSuperadmin = validateUserRole(userRoles.SUPERADMIN);
    const isAdmin = validateUserRole(userRoles.ADMIN);
    const canManageShareAccess = isSuperadmin || isAdmin;
    // Kept for backwards-compat usage below — superadmin-gated UI like the
    // GLOBAL radio still uses this flag.
    const canSeeGlobalOption = isSuperadmin;

    // Owner check — admin can only manage USERS membership on assistants they
    // created. Superadmin can manage any. In create mode, the caller is the
    // implicit owner.
    const isOwner =
        !isEditMode || assistantDetails?.user_id === currentUser?.user_id;
    const canManageUserMembers = isSuperadmin || (isAdmin && isOwner);

    const shareAccessOptions = useMemo(() => {
        const options = [];
        if (isSuperadmin) {
            options.push({ value: shareAccessLevels.GLOBAL, label: "Global" });
        }
        if (canManageShareAccess) {
            options.push({ value: shareAccessLevels.FACILITY, label: "Facility" });
            options.push({ value: shareAccessLevels.USERS, label: "Specific users" });
        }
        return options;
    }, [isSuperadmin, canManageShareAccess, assistantDetails?.share_access]);

    useEffect(() => {
        fetchPromptlist();
    }, []);

    useEffect(() => {
        if (!assistantId) return;

        const fetchAssistantDetails = async () => {
            try {
                setPageLoader(true);
                const data = await assistantService.getAssistantDetails(assistantId);
                setAssistantDetails(data);
                updateForm(data);
                setPageLoader(false);
            } catch (error) {
                setPageLoader(false);
                console.error("Error fetching assistant:", error);
            }
        };

        const fetchAssistantFacilityList = async () => {
            try {
                setPageLoader(true);
                const data = await assistantService.getAssistantFacilityAccessList(assistantId);
                setSelectedFacilities(data?.facility_list || []);
                setInitialSelectedFacilities(data?.facility_list || []);
                setPageLoader(false);
            } catch (error) {
                setPageLoader(false);
                console.error("Error fetching assistant:", error);
            }
        }

        fetchAssistantDetails();
        fetchAssistantFacilityList();
    }, [assistantId]);

    // Load explicit user list once we know the assistant is in USERS mode.
    useEffect(() => {
        if (!assistantId) return;
        if (assistantDetails?.share_access !== shareAccessLevels.USERS) return;

        (async () => {
            try {
                const data =
                    await assistantService.getAssistantUserAccessList(assistantId);
                const list = data?.user_list || [];
                setSelectedUsers(list);
                setInitialSelectedUsers(list);
            } catch (error) {
                console.error("Error fetching assistant user access list:", error);
                toast.error(
                    error?.response?.data?.detail ||
                        error?.response?.data?.message ||
                        "Failed to load assistant members",
                );
            }
        })();
    }, [assistantId, assistantDetails?.share_access]);

    useEffect(() => {
        if (!selectedModel) return

        if (form.context_length > selectedModel?.global_max_tokens) {
            update("context_length", selectedModel?.global_max_tokens)
        }

        // If model doesn't support structured output and json_object is selected, reset
        if (selectedModel.supports_structured_output === false && form.response_format === "json_object") {
            prevResponseFormat.current = form.response_format
            update("response_format", "default")
        }
    }, [selectedModel])

    const updateForm = (data) => {
        const responseFormat = data.response_format || "default";
        setForm({
            name: data.name || "",
            description: data.description || "",
            system_instructions: data.system_instructions || "",
            model_id: data.model_id || "",
            model_configuration_code: data.model_configuration_code || "",
            temperature: data.temperature ?? 0.7,
            context_length: data.context_length ?? 4096,
            top_k: data.top_k ?? 40,
            response_format: responseFormat,
            output_schema: hydrateSchema(data.output_schema),
            share_access: data.share_access || "self",
            is_phi: data.is_phi ?? false,
            tools: data.tools || [],
            is_image_mode: data.is_image_mode ?? false,
            category_ids: data.category_ids || [],
        });

        // Set the ref so we can detect format changes during editing
        prevResponseFormat.current = responseFormat;

        setIsPublished(data.is_published ?? false);
        setShareAccess(data.share_access);

    };


    useEffect(() => {
        const fullModel = llmModels.find(
            (m) => m.global_model_id === assistantDetails?.model_id
        );

        setSelectedModel(fullModel || null);
    }, [assistantDetails, llmModels])

    const getUpdatedFields = () => {
        if (!assistantDetails) return {};

        const updatedFields = {};

        // Normalize original arrays
        const normalizeArray = (val) => val ?? [];

        Object.keys(form).forEach((key) => {
            // output_schema is handled separately below
            if (key === "output_schema") return;

            const currentValue = form[key];
            const originalValue = assistantDetails[key];

            // Handle arrays safely
            if (Array.isArray(currentValue)) {
                const currentArr = normalizeArray(currentValue);
                const originalArr = normalizeArray(originalValue);

                const isSame =
                    currentArr.length === originalArr.length &&
                    currentArr.every((v, i) => v === originalArr[i]);

                if (!isSame) {
                    updatedFields[key] = currentArr;
                }
            }

            // Handle primitives
            else if (currentValue !== originalValue) {
                updatedFields[key] = currentValue;
            }
        });

        // Handle output_schema separately — canonicalize both sides and only
        // include it when the normalized form actually differs. This avoids
        // firing an update API call when the schema is untouched.
        if (form.response_format === "json_object") {
            const currentCanonical = canonicalSchema(form.output_schema);
            const initialCanonical = canonicalSchema(assistantDetails.output_schema);
            if (currentCanonical && !deepEqualUnordered(currentCanonical, initialCanonical)) {
                updatedFields.output_schema = currentCanonical;
            }
        }

        // If switching away from json_object, clear schema
        if (
            assistantDetails.response_format === "json_object" &&
            form.response_format !== "json_object"
        ) {
            updatedFields.clear_output_schema = true;
        }

        // Model comparison (only if selectedModel exists)
        if (selectedModel) {
            if (selectedModel.global_model_id !== assistantDetails.model_id) {
                updatedFields.model_id = selectedModel.global_model_id;
            }

            if (
                selectedModel.model_configuration_code !==
                assistantDetails.model_configuration_code
            ) {
                updatedFields.model_configuration_code =
                    selectedModel.model_configuration_code;
            }
        }

        // is_published toggle
        if (isPublished !== assistantDetails.is_published) {
            updatedFields.is_published = isPublished;
        }

        if (shareAccess !== assistantDetails.share_access) {
            updatedFields.share_access = shareAccess;
        }

        return updatedFields;
    };


    const getFacilityListDelta = () => {
        let current = selectedFacilities || [];
        const initial = initialSelectedFacilities || [];

        if (shareAccess == "global") {
            current = []
        }

        const newAddedFacility = current.filter(
            (id) => !initial.includes(id)
        );

        const removedFacility = initial.filter(
            (id) => !current.includes(id)
        );

        if (newAddedFacility.length === 0 && removedFacility.length === 0) {
            return null;
        }

        return {
            newAddedFacility,
            removedFacility,
        };
    };

    // Guards transitions away from USERS-mode in edit mode behind a
    // confirmation dialog — the explicit list is destroyed by the backend
    // reconciler and the caller should know.
    const handleShareAccessChange = (newMode) => {
        if (newMode === shareAccess) return;

        const leavingUsers =
            isEditMode &&
            assistantDetails?.share_access === shareAccessLevels.USERS &&
            shareAccess === shareAccessLevels.USERS &&
            newMode !== shareAccessLevels.USERS;

        if (leavingUsers) {
            setPendingShareAccessChange(newMode);
            return;
        }
        setShareAccess(newMode);
    };

    const getUserListDelta = () => {
        const current = selectedUsers || [];
        const initial = initialSelectedUsers || [];

        const newAddedUsers = current.filter((id) => !initial.includes(id));
        const removedUsers = initial.filter((id) => !current.includes(id));

        if (newAddedUsers.length === 0 && removedUsers.length === 0) {
            return null;
        }
        return { newAddedUsers, removedUsers };
    };



    return (
        <div className="min-h-screen bg-gray-50 py-4 px-3 sm:py-10 sm:px-6">
            <div className="max-w-3xl mx-auto bg-white p-4 sm:p-8 rounded-2xl shadow-sm border">
                <h2 className="text-xl sm:text-2xl font-semibold mb-6 sm:mb-8">{mode} Assistant</h2>

                {/* Name */}
                <div className="mb-6">
                    <label className="font-medium">
                        Name <span className="text-red-500">*</span>
                    </label>

                    <input
                        maxLength={NAME_MAX_LENGTH}
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        className={`w-full mt-2 border rounded-lg px-4 py-2 ${submitted &&
                            (form.name.length < NAME_MIN_LENGTH ||
                                form.name.length > NAME_MAX_LENGTH)
                            ? "border-red-500"
                            : "border-gray-300"
                            }`}
                        placeholder="Name your Assistant"
                    />

                    <div className="mt-1 flex items-center justify-between text-sm">
                        {/* Left: error */}
                        <span className="min-h-[18px] text-red-500">
                            {submitted &&
                                (form.name.length < NAME_MIN_LENGTH ||
                                    form.name.length > NAME_MAX_LENGTH) && (
                                    <>Name must be between {NAME_MIN_LENGTH} and {NAME_MAX_LENGTH} characters</>
                                )}
                        </span>

                        {/* Right: counter */}
                        <span className="text-gray-400">
                            {form.name.length}/{NAME_MAX_LENGTH}
                        </span>
                    </div>
                </div>


                {/* Description */}
                <div className="mb-6">
                    <label className="font-medium">
                        Description <span className="text-red-500">*</span>
                    </label>

                    <input
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        value={form.description}
                        onChange={(e) => update("description", e.target.value)}
                        className={`w-full mt-2 border rounded-lg px-4 py-2 ${submitted &&
                            (form.description.length < DESCRIPTION_MIN_LENGTH ||
                                form.description.length > DESCRIPTION_MAX_LENGTH)
                            ? "border-red-500"
                            : "border-gray-300"
                            }`}
                        placeholder={`Description must be between ${DESCRIPTION_MIN_LENGTH} and ${DESCRIPTION_MAX_LENGTH} characters`}
                    />

                    <div className="mt-1 flex items-center justify-between text-sm">
                        {/* Left: error */}
                        <span className="min-h-[18px] text-red-500">
                            {submitted &&
                                (form.description.length < DESCRIPTION_MIN_LENGTH ||
                                    form.description.length > DESCRIPTION_MAX_LENGTH) && (
                                    <>
                                        Description must be between {DESCRIPTION_MIN_LENGTH} and{" "}
                                        {DESCRIPTION_MAX_LENGTH} characters
                                    </>
                                )}
                        </span>

                        {/* Right: counter */}
                        <span className="text-gray-400">
                            {form.description.length}/{DESCRIPTION_MAX_LENGTH}
                        </span>
                    </div>
                </div>


                {/* Instructions */}
                <div className="mt-6">
                    {/* Label */}
                    <label className="block font-medium mb-1">
                        Instructions <span className="text-red-500">*</span>
                    </label>

                    {/* Template Selector Row */}
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2 sm:gap-3">
                        <span className="text-xs text-gray-500">
                            Start from a template or write your own
                        </span>

                        <div className="w-full sm:w-[260px]">
                            <Select
                                options={promptOptions}
                                placeholder="Search prompt templates"
                                isSearchable
                                isClearable
                                styles={singleSelectStyles}
                                onChange={(option) => {
                                    if (!option) return;
                                    update("system_instructions", option.content);
                                }}
                                getOptionLabel={(e) => e.label}
                                getOptionValue={(e) => e.value}
                            />
                        </div>
                    </div>

                    {/* Textarea */}
                    <div className="relative">
                        <textarea
                            placeholder={`Please provide Instruction with a minimum length of ${MIN_ASSISTANT_INSTRUCTIONS_LENGTH} characters. (What does this Assistant do? How does it behave? What should it avoid doing?)`}
                            className={`w-full border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-black py-3 px-4 mt-1 h-[150px] pr-10 resize-none ${submitted &&
                                form.system_instructions.length < MIN_ASSISTANT_INSTRUCTIONS_LENGTH
                                ? "border-red-500"
                                : "border-gray-300"
                                }`}
                            value={form.system_instructions}
                            onChange={(e) =>
                                update("system_instructions", e.target.value)
                            }
                        />

                        {/* Regenerate Button */}
                        <button
                            type="button"
                            title="Improve instructions using Copilot"
                            onClick={handleRegenerateInstructions}
                            className="absolute bottom-3 right-3 text-gray-500 hover:text-black transition"
                        >
                            {/* SVG Copilot*/}
                            <svg stroke="currentColor" fill="currentColor" strokeWidth={0} viewBox="0 0 24 24" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M9.75 14a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 .75-.75Zm4.5 0a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 .75-.75Z"></path><path d="M12 2c2.214 0 4.248.657 5.747 1.756.136.099.268.204.397.312.584.235 1.077.546 1.474.952.85.869 1.132 2.037 1.132 3.368 0 .368-.014.733-.052 1.086l.633 1.478.043.022A4.75 4.75 0 0 1 24 15.222v1.028c0 .529-.309.987-.565 1.293-.28.336-.636.653-.966.918a13.84 13.84 0 0 1-1.299.911l-.024.015-.006.004-.039.025c-.223.135-.45.264-.68.386-.46.245-1.122.571-1.941.895C16.845 21.344 14.561 22 12 22c-2.561 0-4.845-.656-6.479-1.303a19.046 19.046 0 0 1-1.942-.894 14.081 14.081 0 0 1-.535-.3l-.144-.087-.04-.025-.006-.004-.024-.015a13.16 13.16 0 0 1-1.299-.911 6.913 6.913 0 0 1-.967-.918C.31 17.237 0 16.779 0 16.25v-1.028a4.75 4.75 0 0 1 2.626-4.248l.043-.022.633-1.478a10.195 10.195 0 0 1-.052-1.086c0-1.331.282-2.498 1.132-3.368.397-.406.89-.717 1.474-.952.129-.108.261-.213.397-.312C7.752 2.657 9.786 2 12 2Zm-8 9.654v6.669a17.59 17.59 0 0 0 2.073.98C7.595 19.906 9.686 20.5 12 20.5c2.314 0 4.405-.594 5.927-1.197a17.59 17.59 0 0 0 2.073-.98v-6.669l-.038-.09c-.046.061-.095.12-.145.177-.793.9-2.057 1.259-3.782 1.259-1.59 0-2.738-.544-3.508-1.492a4.323 4.323 0 0 1-.355-.508h-.344a4.323 4.323 0 0 1-.355.508C10.704 12.456 9.555 13 7.965 13c-1.725 0-2.989-.359-3.782-1.259a3.026 3.026 0 0 1-.145-.177Zm6.309-1.092c.445-.547.708-1.334.851-2.301.057-.357.087-.718.09-1.079v-.031c-.001-.762-.166-1.26-.43-1.568l-.008-.01c-.341-.391-1.046-.689-2.533-.529-1.505.163-2.347.537-2.824 1.024-.462.473-.705 1.18-.705 2.32 0 .605.044 1.087.135 1.472.092.384.231.672.423.89.365.413 1.084.75 2.657.75.91 0 1.527-.223 1.964-.564.14-.11.268-.235.38-.374Zm2.504-2.497c.136 1.057.403 1.913.878 2.497.442.545 1.134.938 2.344.938 1.573 0 2.292-.337 2.657-.751.384-.435.558-1.151.558-2.361 0-1.14-.243-1.847-.705-2.319-.477-.488-1.318-.862-2.824-1.025-1.487-.161-2.192.139-2.533.529-.268.308-.437.808-.438 1.578v.02c.002.299.023.598.063.894Z"></path></svg>
                        </button>

                        {/* Loading Overlay */}
                        {isRegenerating && (
                            <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-xl">
                                <div className="animate-spin h-6 w-6 border-2 border-gray-400 border-t-transparent rounded-full"></div>
                            </div>
                        )}
                    </div>

                    {/* Bottom Row */}
                    <div className="mt-1 flex items-center justify-between text-xs">
                        {/* Left: error */}
                        <span className="min-h-[18px] text-red-500">
                            {submitted &&
                                form.system_instructions.length < MIN_ASSISTANT_INSTRUCTIONS_LENGTH && (
                                    <>
                                        Instructions must be at least{" "}
                                        {MIN_ASSISTANT_INSTRUCTIONS_LENGTH} characters
                                    </>
                                )}
                        </span>

                        {/* Right: counter */}
                        <span className="text-gray-500">
                            {form.system_instructions.length} character
                            {form.system_instructions.length !== 1 ? "s" : ""}
                        </span>
                    </div>
                </div>


                {/* Files */}
                {/* <div className="mt-6 mb-6">
                    <label className="font-medium">
                        Files ({form.file_ids?.length}/10 selected)
                    </label>

                    <div className="border-dashed border-2 rounded-xl p-6 text-center mt-3 cursor-pointer hover:bg-gray-50">
                        <input
                            type="file"
                            multiple
                            className="hidden"
                            id="fileUpload"
                            onChange={handleFileUpload}
                        />
                        <label htmlFor="fileUpload" className="cursor-pointer">
                            Click to add multiple files
                        </label>
                    </div>
                </div> */}

                {/* Image Mode */}
                <div className="mb-6">
                    <label className="block font-medium">Image Mode</label>
                    <span className="text-xs text-gray-500">
                        Enable image generation capabilities for this assistant.
                    </span>

                    <label
                        className={`mt-2 flex items-center gap-3 border rounded-lg px-4 py-3 cursor-pointer transition
                            ${form.is_image_mode
                                ? "border-red-400 bg-red-50"
                                : "border-gray-300 bg-white"
                            }`}
                    >
                        <input
                            type="checkbox"
                            checked={form.is_image_mode}
                            onChange={() => {
                                const newImageMode = !form.is_image_mode;
                                setForm(prev => ({
                                    ...prev,
                                    is_image_mode: newImageMode,
                                    tools: newImageMode ? [] : prev.tools,
                                    response_format: newImageMode ? "default" : prev.response_format,
                                }));
                                setSelectedModel(null);
                            }}
                            className="accent-red-500 h-4 w-4"
                        />
                        <span className="text-sm font-medium text-slate-700">
                            Enable Image Generation
                        </span>
                    </label>
                </div>

                {/* Model */}
                <div className="mb-6">
                    <label className="block font-medium">
                        Recommended Model <span className="text-red-500">*</span>
                    </label>
                    <span className="text-xs text-gray-500">
                        Recommend a model to the user, which should be used by default for best results.
                    </span>
                    <ModelPicker
                        label=""
                        selected={selectedModel}
                        onChange={(model) => setSelectedModel(model)}
                        isImageMode={form.is_image_mode}
                    />
                </div>

                {/* Response Format */}
                <div className="mt-6">
                    <label className="font-medium">Response format</label>
                    <Select
                        options={responseFormatOptions}
                        placeholder="Select response format"
                        isSearchable
                        isClearable={false}
                        isDisabled={form.is_image_mode}
                        styles={singleSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        value={responseFormatOptions.find(
                            opt => opt.value === form.response_format
                        )}
                        onChange={(option) => {
                            if (!option) return
                            // Track the previous format before updating
                            prevResponseFormat.current = form.response_format
                            update("response_format", option.value)
                        }}
                    />

                    {/* Schema Builder — only when json_object is selected */}
                    {form.response_format === "json_object" && (
                        <SchemaBuilder
                            schema={form.output_schema}
                            onChange={(newSchema) => update("output_schema", newSchema)}
                            errors={schemaErrors}
                        />
                    )}
                </div>

                {/* Sliders */}
                <div className="mt-6 mb-6">
                    <label>Temperature: {form.temperature}</label>
                    <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={form.temperature}
                        onChange={(e) =>
                            update("temperature", parseFloat(e.target.value))
                        }
                        className="w-full relative z-0 pointer-events-auto accent-blue-500"
                    />
                </div>

                <div className="mb-6">
                    <label>Top K: {form.top_k}</label>
                    <input
                        type="range"
                        min="1"
                        max="100"
                        value={form.top_k}
                        onChange={(e) =>
                            update("top_k", parseInt(e.target.value))
                        }
                        className="w-full relative z-0 pointer-events-auto accent-blue-500"
                    />
                </div>

                <div className="mb-6">
                    <label title="The maximum number of tokens the model can generate in a single response.">
                        Output Context Length: {form.context_length}
                    </label>
                    <input
                        type="range"
                        min="1024"
                        max={maxContextLength}
                        step="1024"
                        value={form.context_length}
                        onChange={(e) =>
                            update("context_length", parseInt(e.target.value))
                        }
                        className="w-full relative z-0 pointer-events-auto accent-blue-500"
                    />
                </div>

                {/* Tools — hidden when image mode is enabled */}
                {!form.is_image_mode && (
                    <div className="mb-8">
                        <label className="mb-2 block font-medium">Tools</label>

                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                className="accent-blue-500"
                                checked={form.tools?.includes("web_search")}
                                onChange={(e) => {
                                    const checked = e.target.checked;

                                    setForm(prev => ({
                                        ...prev,
                                        tools: checked
                                            ? [...(prev.tools || []), "web_search"] // add
                                            : (prev.tools || []).filter(t => t !== "web_search") // remove
                                    }));
                                }}
                            />
                            Web Search
                        </label>
                    </div>
                )}

                {/* Category overrides — which interaction categories this assistant's
                    messages classify against. Selecting fewer than 2 (or none) falls
                    back to the global default taxonomy automatically. */}
                <div className="mb-8">
                    <label className="mb-2 block font-medium">Category overrides</label>
                    <p className="text-xs text-slate-500 mb-2">
                        Classify this assistant's interactions against exactly these categories
                        instead of the global defaults. Pick at least 2 — fewer than that, the
                        global default categories are used instead. You can add back any of the
                        default categories here too.
                    </p>
                    <Select
                        isMulti
                        options={categoryOptions.map((c) => ({
                            value: c.id,
                            label: c.is_active ? c.name : `${c.name} (inactive)`,
                        }))}
                        value={categoryOptions
                            .filter((c) => form.category_ids?.includes(c.id))
                            .map((c) => ({
                                value: c.id,
                                label: c.is_active ? c.name : `${c.name} (inactive)`,
                            }))}
                        onChange={(vals) => update("category_ids", vals.map((v) => v.value))}
                        placeholder="Default categories apply — select to override"
                        classNamePrefix="react-select"
                    />
                </div>


                {/* Share Access */}
                {canManageShareAccess && (
                    <div className="mb-6">
                        <label className="block mb-3 text-md font-medium text-slate-700">
                            Share access
                        </label>

                        <div className="flex flex-wrap gap-x-6 gap-y-3">
                            {shareAccessOptions.map((opt) => (
                                <label
                                    key={opt.value}
                                    className="flex items-center gap-2 cursor-pointer"
                                >
                                    <input
                                        type="radio"
                                        className="accent-blue-500"
                                        name="shareAccess"
                                        value={opt.value}
                                        checked={shareAccess === opt.value}
                                        onChange={() => handleShareAccessChange(opt.value)}
                                    />
                                    <span className="text-sm text-slate-700">{opt.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {/* 🏥 Facility Multi-select */}
                {(canManageShareAccess && shareAccess === shareAccessLevels.FACILITY) && (
                    <div className="mb-4">
                        <Select
                            isMulti
                            options={facilityOptions}
                            value={facilityOptions.filter((o) =>
                                selectedFacilities.includes(o.value)
                            )}
                            onChange={(vals) =>
                                setSelectedFacilities(vals.map((v) => v.value))
                            }
                            placeholder="Select facilities"
                            classNamePrefix="react-select"
                        />
                    </div>
                )}

                {/* 👥 User Multi-select (USERS share_access) */}
                {(canManageShareAccess && shareAccess === shareAccessLevels.USERS) && (
                    <div className="mb-4 space-y-2">
                        <UserAccessPicker
                            value={selectedUsers}
                            onChange={setSelectedUsers}
                            users={facilityUserList}
                            facilityList={facilityList}
                            isSuperadmin={isSuperadmin}
                            callerFacilityId={
                                Number.isNaN(userFacilityId) ? null : userFacilityId
                            }
                            disabled={isEditMode && !canManageUserMembers}
                            hasError={
                                submitted &&
                                shareAccess === shareAccessLevels.USERS &&
                                selectedUsers.length === 0
                            }
                            placeholder={
                                isSuperadmin
                                    ? "Select users from any facility"
                                    : "Select users from your facility"
                            }
                        />
                        {submitted &&
                            shareAccess === shareAccessLevels.USERS &&
                            selectedUsers.length === 0 && (
                                <p className="text-xs text-red-500">
                                    Please select at least one user
                                </p>
                            )}
                        {isEditMode &&
                            isAdmin &&
                            assistantDetails?.share_access ===
                                shareAccessLevels.USERS && (
                                <p className="text-xs text-gray-500">
                                    Some users on this assistant may have been granted by a
                                    superadmin and are not visible to you.
                                </p>
                            )}
                        {isEditMode && !canManageUserMembers && (
                            <p className="text-xs text-amber-600">
                                Only the assistant&apos;s creator can manage members.
                            </p>
                        )}
                    </div>
                )}

                {/* Transition confirmation — when leaving USERS mode */}
                {pendingShareAccessChange && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                        <div className="bg-white rounded-lg p-4 sm:p-6 mx-3 w-full max-w-md shadow-xl">
                            <h3 className="text-lg font-semibold mb-3">
                                Change share access?
                            </h3>
                            <p className="text-sm text-gray-700">
                                {pendingShareAccessChange === shareAccessLevels.SELF
                                    ? `Switching to private will revoke access for all ${initialSelectedUsers.length} explicitly-shared users. Continue?`
                                    : `Switching to ${
                                          pendingShareAccessChange ===
                                          shareAccessLevels.GLOBAL
                                              ? "Global"
                                              : "Facility"
                                      } will remove the explicit user list. Members will keep access only if they are part of the new ${
                                          pendingShareAccessChange ===
                                          shareAccessLevels.GLOBAL
                                              ? "everyone"
                                              : "facility"
                                      }. Continue?`}
                            </p>
                            <div className="flex justify-end gap-2 mt-6">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setPendingShareAccessChange(null)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    className="bg-blue-500 text-white hover:bg-blue-400"
                                    onClick={() => {
                                        setShareAccess(pendingShareAccessChange);
                                        setPendingShareAccessChange(null);
                                    }}
                                >
                                    Continue
                                </Button>
                            </div>
                        </div>
                    </div>
                )}



                {/* Update files: heading + description + link to the assistant's files page */}
                {isEditMode && (
                    <div className="mb-8">
                        <label className="mb-2 block font-medium">Update files</label>
                        <p className="text-xs text-slate-500 mb-2">
                            Add, view or remove the files this assistant uses as its knowledge.
                            Changes made on this page are not saved when you open the files page,
                            so save your edits first.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate(`/update-assistant-files/${assistantId}`)}
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                            Go to Update Assistant Files &rarr;
                        </button>
                    </div>
                )}

                {/* Publish toggle */}
                <div className="flex items-center gap-3 mb-8">
                    <button
                        type="button"
                        onClick={() => setIsPublished(!isPublished)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition
              ${isPublished ? "bg-indigo-600" : "bg-gray-200"}`}
                    >
                        <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white transition
                ${isPublished ? "translate-x-5" : "translate-x-1"}`}
                        />
                    </button>
                    <span className="text-md font-medium text-slate-700">
                        Publish
                    </span>
                </div>


                {/* Submit */}
                <div className="flex justify-end">
                    <button
                        onClick={handleSubmit}
                        className="flex-1 sm:flex-none bg-blue-500 hover:bg-blue-300 text-white px-6 py-2 rounded-lg"
                    >
                        {mode}
                    </button>
                    <button
                        onClick={() => navigate("/assistants")}
                        className="flex-1 sm:flex-none bg-gray-500 hover:bg-gray-300 text-white px-6 py-2 rounded-lg ml-2"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}
